/**
 * Tool page configs — one config per route.
 * Drives the Tool page renderer.
 */
const WHOP = "https://whop.com/checkout/plan_hfsvR7lsOjQds";

export const TOOL_PAGES = {
  jobs: {
    title: "Job Board",
    nav: { label: "Job Board" },
    hero: {
      kicker: "JOB BOARD · FREE WITH AN ACCOUNT",
      title: (
        <>
          Stop searching.
          <br />
          <span className="bg-[#FFE600] px-2">Start executing.</span>
        </>
      ),
      sub: "338,947+ live ATS jobs pulled straight from 18,000+ company boards — Greenhouse, Lever, Ashby, Workday, SmartRecruiters, Workable. Organized around your profile, direct-apply. No reposts, no scrapers, no spam.",
      image:
        "https://storage.googleapis.com/jobhacki-assets/site/demo-tools/v2/jobs.png",
      statNum: "338K+",
      statLabel: "LIVE ATS JOBS",
      primary: { label: "Open the Job Directory", href: "/login" },
      secondary: { label: "GET ALL TOOLS", href: WHOP },
    },
    sections: [
      {
        type: "threeCards",
        kicker: "HOW THE LISTINGS REACH YOU",
        title: (
          <>
            The board <span className="bg-[#FFE600] px-2">comes to you.</span>
          </>
        ),
        cards: [
          {
            title: "Tracked at the source",
            body: "First-party ATS feeds, refreshed continuously. The same posting a recruiter just published — you see it within the hour.",
          },
          {
            title: "Hyper-filtered to you",
            body: "Level, stack, location, remote, visa. Saved searches, alerts and a built-in pipeline tracker — no LinkedIn-feed noise.",
          },
          {
            title: "No scroll, no spam",
            body: "Direct-apply where employers actually read. No 'easy apply' black hole. No reposts. No 'sponsored by an MBA grad' overlay.",
          },
        ],
      },
      { type: "sourceLogos" },
      { type: "liveDemo" },
      { type: "comparison" },
      { type: "whoFor" },
      { type: "pricingBand" },
      {
        type: "finalCta",
        kicker: "THE LISTINGS ARE LIVE. GO GET ONE.",
        title: (
          <>
            Stop searching.
            <br />
            <span className="bg-[#0a0a0a] text-[#FFE600] px-3">
              Start executing.
            </span>
          </>
        ),
        ctaPrimary: { label: "Open the Job Directory", href: "/login" },
        ctaSecondary: { label: "GET ALL TOOLS", href: WHOP },
      },
    ],
  },

  simulator: {
    title: "Readiness Simulator",
    nav: { label: "Readiness Simulator" },
    hero: {
      kicker: "READINESS SIMULATOR · ALL-ACCESS",
      title: (
        <>
          Paste a job.
          <br />
          Find out if you&apos;d{" "}
          <span className="bg-[#FF3B30] text-white px-2">survive it.</span>
        </>
      ),
      sub: "Know where you stand before the interview does. The simulator scores you against the actual posting and hands back a study plan. Voice + text. Teach-back grading until you stop fumbling.",
      image:
        "https://storage.googleapis.com/jobhacki-assets/whop-carousel/04_readiness_simulator.png",
      statNum: "0-100",
      statLabel: "READINESS SCORE",
      primary: { label: "GET ALL-ACCESS FOR LIFE", href: WHOP, icon: "lock" },
      secondary: { label: "Start free", href: "/login" },
    },
    sections: [
      {
        type: "threeCards",
        kicker: "HOW IT WORKS",
        title: (
          <>
            Three moves.{" "}
            <span className="bg-[#FFE600] px-2">Zero excuses.</span>
          </>
        ),
        cards: [
          {
            title: "Paste any posting",
            body: "Drop a Greenhouse / Lever / Workday URL or raw JD text. The simulator builds the test from the posting itself.",
          },
          {
            title: "Get tested",
            body: "Voice or text. Behavioral, technical, scenario. Scored on substance, structure, and delivery — not vibes.",
          },
          {
            title: "Leave with a study plan",
            body: "A scored gap list and a teach-back plan you can drill until the morning of the interview.",
          },
        ],
      },
      { type: "comparison" },
      { type: "pricingBand" },
      {
        type: "finalCta",
        kicker: "TEST YOURSELF BEFORE THEY DO",
        title: (
          <>
            Walk in <span className="bg-[#0a0a0a] text-[#FFE600] px-3">calm.</span>
          </>
        ),
        ctaPrimary: { label: "GET ALL-ACCESS FOR LIFE", href: WHOP },
        ctaSecondary: { label: "Start free", href: "/login" },
      },
    ],
  },

  toolbox: {
    title: "The Toolbox",
    nav: { label: "Toolbox" },
    hero: {
      kicker: "THE TOOLBOX · FREE WITH AN ACCOUNT",
      title: (
        <>
          Every tool, prompt & repo
          <br />
          from every video —{" "}
          <span className="bg-[#FFE600] px-2">one searchable directory.</span>
        </>
      ),
      sub: "Stop hunting through video descriptions. 252 deduped entries across 86 categories — 192 Tools · 35 Prompts · 23 Repos · 2 GPTs. Each linked to the playbook that uses it. Free with an account.",
      image:
        "https://storage.googleapis.com/jobhacki-assets/site/demo-tools/v2/toolbox.png",
      statNum: "252",
      statLabel: "ENTRIES · 86 CATEGORIES",
      primary: { label: "Open the toolbox", href: "/login" },
      secondary: { label: "GET ALL TOOLS", href: WHOP },
    },
    sections: [
      {
        type: "threeCards",
        kicker: "WHY IT EXISTS",
        title: (
          <>
            Not a list of links.{" "}
            <span className="bg-[#FFE600] px-2">A working stack.</span>
          </>
        ),
        cards: [
          {
            title: "252 real entries, deduped",
            body: "Every tool, prompt, repo and GPT used across the playbooks. Cleaned, categorized, search-indexed. No dead links.",
          },
          {
            title: "Free with an account",
            body: "No credit card. No paywall. The entire directory unlocks the moment you log in.",
          },
          {
            title: "Linked to the playbooks",
            body: "Each tool shows the playbook that uses it. Pop from a tool into the exact step where it earns its keep.",
          },
        ],
      },
      {
        type: "upsellBand",
        title: "Want every tool, not just the directory?",
        body: "All-Access (one-time) adds Resume Builder, Simulator, OmniCut, OmniScript and the rest. The directory stays free.",
      },
      {
        type: "finalCta",
        kicker: "OPEN THE TOOLBOX",
        title: (
          <>
            252 entries.{" "}
            <span className="bg-[#0a0a0a] text-[#FFE600] px-3">
              One login.
            </span>
          </>
        ),
        ctaPrimary: { label: "Open the toolbox", href: "/login" },
        ctaSecondary: { label: "GET ALL TOOLS", href: WHOP },
      },
    ],
  },

  playbooks: {
    title: "Playbook Directory",
    nav: { label: "Playbooks" },
    hero: {
      kicker: "PLAYBOOK DIRECTORY · FREE WITH AN ACCOUNT",
      title: (
        <>
          The <span className="bg-[#FFE600] px-2">Playbook Directory.</span>
        </>
      ),
      sub: "100+ step-by-step job & income playbooks. Every guide carries a 'Grounded' score with source-reported numbers. The model, the steps, the order — real methods, never theory.",
      image:
        "https://storage.googleapis.com/jobhacki-assets/site/demo-tools/v2/playbooks.png",
      statNum: "100+",
      statLabel: "GROUNDED PLAYBOOKS",
      primary: { label: "Browse the playbooks", href: "/login" },
      secondary: { label: "GET ALL TOOLS", href: WHOP },
    },
    sections: [
      {
        type: "cardGrid",
        kicker: "FEATURED PLAYBOOKS",
        title: (
          <>
            The model. The steps.{" "}
            <span className="bg-[#0055FF] text-white px-2">The order.</span>
          </>
        ),
        items: [
          {
            tag: "JOB PLAYBOOK",
            title: "Land a Tech Job: 30–90 Day System",
            body: "The exact 30/60/90 sequence — resume, applies, prep, follow-ups — that gets you to onsite.",
            meta: "GROUNDED · 86/100",
          },
          {
            tag: "JOB PLAYBOOK",
            title: "Work-From-Home Jobs: The Real List",
            body: "The companies that actually hire remote, by role and timezone. Updated, with apply paths.",
            meta: "GROUNDED · 78/100",
          },
          {
            tag: "INCOME PLAYBOOK",
            title: "Build & Sell an AI Automation Agency",
            body: "From first client to $10k/mo retainer — niche, offer, outreach, delivery, pricing.",
            meta: "GROUNDED · 82/100",
          },
          {
            tag: "INTERVIEW",
            title: "Master the STAR Method",
            body: "10 behavioral archetypes, drilled with teach-back examples — until you stop saying 'um'.",
            meta: "GROUNDED · 91/100",
          },
          {
            tag: "INCOME PLAYBOOK",
            title: "Launch an AI Receptionist Business",
            body: "$2.5k–$5k/mo retainers. Stack, outreach script, pricing tiers, common objections.",
            meta: "GROUNDED · 79/100",
          },
          {
            tag: "RESUME",
            title: "The Hyperlink Resume",
            body: "Embed certifications, GitHub, deployed projects — the move that gets you past the ATS.",
            meta: "GROUNDED · 88/100",
          },
        ],
      },
      {
        type: "accessBand",
        title: "Free with an account.",
        body: "The full directory is free to browse the moment you log in. All-Access unlocks the gated tools that pair with it.",
      },
      { type: "pricingBand" },
      {
        type: "finalCta",
        kicker: "BROWSE + START EXECUTING",
        title: (
          <>
            Stop watching.{" "}
            <span className="bg-[#0a0a0a] text-[#FFE600] px-3">Start shipping.</span>
          </>
        ),
        ctaPrimary: { label: "Browse the playbooks", href: "/login" },
        ctaSecondary: { label: "GET ALL TOOLS", href: WHOP },
      },
    ],
  },

  omnicut: {
    title: "OmniCut",
    nav: { label: "OmniCut" },
    hero: {
      kicker: "OMNICUT · ALL-ACCESS",
      title: (
        <>
          Drop in a video.{" "}
          <span className="bg-[#FF00FF] text-white px-2">
            Get the edit blueprint back.
          </span>
        </>
      ),
      sub: "From raw footage to a cut plan you can execute — a timestamped blueprint via a custom GPT, already built. Hook-first. Frame-by-frame. Print attention on demand.",
      image:
        "https://storage.googleapis.com/jobhacki-assets/whop-carousel/03_omnicut.png",
      statNum: "GPT",
      statLabel: "ALREADY BUILT",
      primary: { label: "GET ALL-ACCESS FOR LIFE", href: WHOP, icon: "lock" },
      secondary: { label: "Start free", href: "/login" },
    },
    sections: [
      {
        type: "threeCards",
        kicker: "WHAT YOU GET",
        title: (
          <>
            Your next edit,{" "}
            <span className="bg-[#FFE600] px-2">already mapped.</span>
          </>
        ),
        cards: [
          {
            title: "Timestamped blueprint",
            body: "A per-frame cut plan — hook, beats, payoff. With timestamps you can paste straight into your editor.",
          },
          {
            title: "Custom GPT, already built",
            body: "No prompt engineering, no jailbreaks. Open the GPT, drop the file, get the blueprint.",
          },
          {
            title: "Structure for the edit",
            body: "Built on the patterns that actually go viral — pacing, payoff, retention curves. Not theory.",
          },
        ],
      },
      { type: "pricingBand" },
      {
        type: "finalCta",
        kicker: "YOUR NEXT EDIT, ALREADY MAPPED",
        title: (
          <>
            Ship the cut.{" "}
            <span className="bg-[#0a0a0a] text-[#FFE600] px-3">Print attention.</span>
          </>
        ),
        ctaPrimary: { label: "GET ALL-ACCESS FOR LIFE", href: WHOP },
        ctaSecondary: { label: "Start free", href: "/login" },
      },
    ],
  },

  omniscript: {
    title: "OmniScript",
    nav: { label: "OmniScript" },
    hero: {
      kicker: "OMNISCRIPT · SOON · ALL-ACCESS WAITLIST",
      title: (
        <>
          OmniScript:{" "}
          <span className="bg-[#FFE600] px-2">the AI script engine.</span>
        </>
      ),
      sub: "From topic to finished script, structurally sound — hook-first, in your voice. The script engine that powers the OmniCut edit. Ships next.",
      image:
        "https://storage.googleapis.com/jobhacki-assets/site/demo-tools/v2/omniscript.png",
      statNum: "SOON",
      statLabel: "WAITLIST OPEN",
      primary: { label: "Lock + notify me", href: WHOP, icon: "lock" },
      secondary: { label: "Notify me (free)", href: "/login" },
    },
    sections: [
      {
        type: "threeCards",
        kicker: "WHAT YOU GET",
        title: (
          <>
            Topic in. <span className="bg-[#FF00FF] text-white px-2">Script out.</span>
          </>
        ),
        cards: [
          {
            title: "Hook-first structure",
            body: "Every script opens on a hook that actually retains. Beat sheet, mid-point, payoff — already in place.",
          },
          {
            title: "Your voice, kept",
            body: "Drop in 3 sample scripts. OmniScript matches your phrasing, cadence and jokes. Not generic AI mush.",
          },
          {
            title: "Full scripts out",
            body: "Not bullet outlines — finished, performable scripts you can record straight from the page.",
          },
        ],
      },
      {
        type: "waitlistBand",
        title: "Be first in line when OmniScript ships.",
      },
      {
        type: "finalCta",
        kicker: "GET IN LINE",
        title: (
          <>
            Topic in. Script out.{" "}
            <span className="bg-[#0a0a0a] text-[#FFE600] px-3">
              Shipping soon.
            </span>
          </>
        ),
        ctaPrimary: { label: "Lock + notify", href: WHOP },
        ctaSecondary: { label: "Notify me (free)", href: "/login" },
      },
    ],
  },
};
